import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import InviteCard from "@/components/InviteCard";
import BestTimesCard from "@/components/BestTimesCard";
import CatchphraseCard from "@/components/CatchphraseCard";
import { useAuth } from "@/context/AuthContext";
import { Zap, Flag, ChevronRight, Trophy, Car as CarIcon, Coins, Gauge, Shield, Crown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = "https://images.pexels.com/photos/34958805/pexels-photo-34958805.jpeg";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [cities, setCities] = useState([]);
  const [lobbies, setLobbies] = useState([]);
  const [garage, setGarage] = useState(null);
  const [bossProgress, setBossProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [c, l, g, b] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/cities`),
          axios.get(`${BACKEND_URL}/api/lobbies`),
          axios.get(`${BACKEND_URL}/api/garage`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/boss/progress`, { withCredentials: true }),
        ]);
        setCities(c.data.cities);
        setLobbies(l.data.lobbies);
        setGarage(g.data);
        setBossProgress(b.data);
      } catch (e) {
        console.error(e);
      }
    })();
    refresh();
  }, [refresh]);

  const quickRace = async (cityId) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/lobbies`,
        { city_id: cityId },
        { withCredentials: true }
      );
      navigate(`/lobbies/${res.data.lobby_id}`);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AppLayout>
      <div className="relative">
        {/* Hero header */}
        <div className="relative h-[280px] overflow-hidden border-b border-white/10">
          <img src={HERO_BG} alt="hero" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-[#0A0A0A]" />
          <div className="relative h-full flex items-end px-6 lg:px-12 pb-10">
            <div>
              <div className="label-kicker mb-2">Welcome back, heister</div>
              <h1 className="font-heading text-5xl lg:text-6xl">{user?.name ?? "Racer"}</h1>
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-12 py-10 space-y-12">
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            <StatCell label="Credits" value={user?.currency ?? 0} icon={Coins} accent="#FFCC00" testid="stat-credits" />
            <StatCell label="Competitive Races" value={user?.competitive_wins ?? 0} icon={Trophy} accent="#FF3B30" testid="stat-competitive-wins" />
            <StatCell label="Races" value={user?.races ?? 0} icon={Flag} accent="#007AFF" testid="stat-races" />
            <StatCell
              label="Car HP"
              value={`${garage?.car?.hp ?? 100}/${garage?.car?.max_hp ?? 100}`}
              icon={Shield}
              accent="#34C759"
              testid="stat-hp"
            />
          </div>

          {/* Local Legend badges — earned by visiting every signature prop
              in a city AND collecting all 5 artifacts. */}
          {(user?.badges ?? []).length > 0 && (
            <div className="surface-solid p-6" data-testid="dashboard-badges">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-kicker">Local Legend hall</div>
                  <div className="font-heading text-2xl mt-1">Badges earned</div>
                </div>
                <span className="font-mono text-xs text-white/40">
                  {user.badges.length}/10
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {user.badges.map((b) => (
                  <span
                    key={b}
                    data-testid={`dashboard-badge-${b.replace(/\s+/g, "-").toLowerCase()}`}
                    className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00] font-heading text-sm uppercase tracking-wider"
                  >
                    <Trophy className="w-3 h-3" /> {b}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Invite + Personal Bests — social + replay layer */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <InviteCard />
            <BestTimesCard />
          </div>

          {/* Catchphrase setter */}
          <CatchphraseCard />

          {/* Boss trophy progress card — links into the Boss Gallery. */}
          {bossProgress && (
            <button
              onClick={() => navigate("/bosses")}
              data-testid="dashboard-boss-gallery-card"
              className="w-full text-left surface-solid p-6 border-2 border-transparent hover:border-[#FFCC00] hover:bg-[#FFCC00]/[0.03] transition-all duration-200 group"
            >
              <div className="flex items-center gap-5">
                <div
                  className={`w-16 h-16 flex items-center justify-center border-2 ${
                    bossProgress.has_globetrotter
                      ? "border-[#FFCC00] bg-[#FFCC00]/15 text-[#FFCC00] shadow-[0_0_22px_rgba(255,204,0,0.45)]"
                      : "border-white/15 text-white/60"
                  }`}
                >
                  <Crown className="w-8 h-8" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="label-kicker">Trophy Room</div>
                  <div className="font-heading text-3xl mt-1">Boss Gallery</div>
                  <div className="text-xs text-white/50 mt-1">
                    {bossProgress.defeated_count}/{bossProgress.total} bosses defeated
                    {bossProgress.has_globetrotter && (
                      <span className="ml-2 text-[#FFCC00]">· GLOBETROTTER UNLOCKED</span>
                    )}
                  </div>
                  <div className="mt-3 h-2 bg-white/5 border border-white/10 overflow-hidden max-w-md">
                    <div
                      className="h-full bg-gradient-to-r from-[#FF3B30] via-[#FFCC00] to-[#FFD700] transition-all duration-500"
                      style={{
                        width: `${
                          bossProgress.total > 0
                            ? Math.round((bossProgress.defeated_count / bossProgress.total) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                <ChevronRight className="w-6 h-6 text-white/30 group-hover:text-[#FFCC00] transition-colors" />
              </div>
            </button>
          )}

          {/* Featured race + garage card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 surface-solid p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-kicker">Quick race</div>
                  <div className="font-heading text-3xl mt-1">Pick your city.</div>
                </div>
                <button
                  className="btn-ghost text-xs"
                  data-testid="dashboard-browse-lobbies"
                  onClick={() => navigate("/lobbies")}
                >
                  Browse all <ChevronRight className="w-3 h-3 inline" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {cities.slice(0, 6).map((c) => (
                  <button
                    key={c.id}
                    data-testid={`dashboard-city-${c.id}`}
                    onClick={() => quickRace(c.id)}
                    className="group text-left border border-white/10 hover:border-[#FF3B30] hover:bg-[#FF3B30]/5 p-4 transition-all duration-150"
                  >
                    <div className="w-2 h-2 rounded-full mb-3" style={{ background: c.accent }} />
                    <div className="font-heading text-xl">{c.name}</div>
                    <div className="text-xs text-white/50 mt-1">{c.tagline}</div>
                    <div className="mt-3 font-mono text-[10px] text-white/40 tracking-widest">
                      {c.items_count} ITEMS · 06:20
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="surface-solid p-6 flex flex-col">
              <div className="label-kicker">Your ride</div>
              {garage?.car && (
                <>
                  <div className="font-heading text-3xl mt-1">{garage.car.name}</div>
                  <div className="flex items-center gap-3 mt-4">
                    <span className="w-8 h-8 rounded-sm border border-white/20" style={{ background: garage.car.body_color }} />
                    <span className="w-6 h-6 rounded-sm border border-white/20" style={{ background: garage.car.accent_color }} />
                  </div>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <MiniStat icon={Gauge} label="Speed" value={`L${garage.car.speed_level}`} />
                    <MiniStat icon={Shield} label="Armor" value={`L${garage.car.armor_level}`} />
                  </div>
                  <div className="flex-1" />
                  <button
                    className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
                    onClick={() => navigate("/garage")}
                    data-testid="dashboard-goto-garage"
                  >
                    <CarIcon className="w-4 h-4" /> Enter Garage
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Active lobbies */}
          <div>
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="label-kicker">Live now</div>
                <div className="font-heading text-3xl mt-1">Open Lobbies</div>
              </div>
              <div className="font-mono text-xs text-white/40">{lobbies.length} OPEN</div>
            </div>
            {lobbies.length === 0 ? (
              <div className="surface-solid p-10 text-center">
                <Zap className="w-8 h-8 text-[#FF3B30] mx-auto mb-3" />
                <div className="font-heading text-2xl">No lobbies open</div>
                <div className="text-sm text-white/50 mt-1">Start a race and be the host.</div>
                <button
                  className="btn-primary mt-6"
                  onClick={() => navigate("/lobbies")}
                  data-testid="dashboard-create-first-lobby"
                >
                  Create Lobby
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lobbies.slice(0, 6).map((l) => (
                  <button
                    key={l.lobby_id}
                    data-testid={`dashboard-lobby-${l.lobby_id}`}
                    onClick={() => navigate(`/lobbies/${l.lobby_id}`)}
                    className="text-left border border-white/10 hover:border-[#FF3B30] p-4 transition-colors duration-150 flex items-center justify-between"
                  >
                    <div>
                      <div className="font-heading text-xl">{l.city_name}</div>
                      <div className="text-xs text-white/50">
                        Host: {l.host_name} · {l.players.length} racer{l.players.length > 1 ? "s" : ""}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/40" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function StatCell({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-[#121212] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="label-kicker">{label}</span>
      </div>
      <div className="font-heading text-3xl">{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="border border-white/10 p-3">
      <div className="flex items-center gap-1.5 text-white/60">
        <Icon className="w-3 h-3" />
        <span className="label-kicker">{label}</span>
      </div>
      <div className="font-heading text-xl mt-1">{value}</div>
    </div>
  );
}
